import { Intent } from '@blueprintjs/core';
import { Reaction } from '@retro-ui/api-contract';
import { captureException } from '@sentry/browser';
import { useContext, useEffect, useState } from 'react';

import { apiClient } from '@f/api-client';
import { AppToaster } from '@f/components/notification-toaster/AppToaster';
import { SciFinderAuthContext } from '@f/context';
import { sessionStorageClient } from '@f/services/storage/wrapper';

const BASE_REACTION_KEY = 'scifinder_base_reaction';

const storage = sessionStorageClient;

function showToast(message: string, intent: Intent) {
  AppToaster.show(
    {
      message,
      intent,
    },
    'scifinderReferenceError'
  );
}

export const useSciFinderReference = () => {
  const { accessToken, isAuthenticating, authenticate } = useContext(SciFinderAuthContext);
  const [isOpening, setIsOpening] = useState(false);
  const [reactionToOpen, setReactionToOpen] = useState<Reaction>();

  const openReferences = async (reaction: Reaction) => {
    setIsOpening(true);
    setReactionToOpen(reaction);

    if (!accessToken) {
      storage.setItem(BASE_REACTION_KEY, JSON.stringify(reaction));
      void authenticate();
      return;
    }

    try {
      const { status, body: referencesLink } = await apiClient.referenceReactions.fetchReferenceReactions({
        headers: {
          authorization: `Bearer ${accessToken}`,
        },
        body: reaction,
        redirectUnauthorized: false,
      });

      if (status === 401) {
        storage.setItem(BASE_REACTION_KEY, JSON.stringify(reaction));
        void authenticate();
        return;
      }

      if (status !== 200) {
        showToast('No references have been found.', Intent.WARNING);
        return;
      }

      window.open(referencesLink, '_blank');
    } catch (error: any) {
      const toastMessage = error instanceof Error ? error.message : (error as string);
      showToast(toastMessage, Intent.DANGER);

      captureException(error);
    } finally {
      setIsOpening(false);
      setReactionToOpen(undefined);
    }
  };

  useEffect(() => {
    if (!accessToken) {
      return;
    }

    const baseReaction = storage.getItem(BASE_REACTION_KEY);

    if (!baseReaction) {
      return;
    }

    storage.removeItem(BASE_REACTION_KEY);
    void openReferences(JSON.parse(baseReaction));
  }, [accessToken]);

  return { isLoading: isOpening || isAuthenticating, reactionToOpen, openReferences } as const;
};
